import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ANNUAL_FORECASTS_2027 } from '../data/annualForecasts';
import { SIGNS } from '../data/signs';

export function AnnualForecastPage() {
  const { signo } = useParams<{ signo: string }>();
  const forecast = ANNUAL_FORECASTS_2027.find(f => f.slug === signo);
  const sign = SIGNS.find(s => s.slug === signo);

  if (!forecast || !sign) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: 'var(--text-muted)' }}>Previsão não encontrada.</p>
          <Link to="/" style={{ color: 'var(--petrol)', marginTop: '1rem', display: 'inline-block' }}>← Voltar</Link>
        </div>
      </div>
    );
  }

  const ogImage = `https://professya.com/og/previsao-2027-${sign.slug}.png`;

  return (
    <>
      <Helmet>
        <title>Previsão 2027 para {sign.name}, O que os Astros Reservam, Professya</title>
        <meta name="description" content={forecast.metaDescription} />
        <meta property="og:title" content={`Previsão 2027 para ${sign.name}, Professya`} />
        <meta property="og:description" content={forecast.metaDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={`https://professya.com/previsao-2027/${sign.slug}`} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={`https://professya.com/previsao-2027/${sign.slug}`} />
      </Helmet>

      <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-base)', color: 'var(--text-primary)' }}>
        {/* Hero */}
        <div style={{ background: 'linear-gradient(180deg, rgba(245,200,66,0.1) 0%, transparent 100%)', padding: '3rem 1.5rem 2rem', textAlign: 'center' }}>
          <Link to="/" style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', textDecoration: 'none', display: 'inline-block', marginBottom: '1.5rem' }}>
            ✦ Professya
          </Link>
          <div style={{ fontSize: '3.5rem', marginBottom: '0.75rem' }}>{sign.emoji}</div>
          <p style={{ color: 'var(--petrol)', fontWeight: 600, fontSize: 'var(--text-label)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.25rem' }}>
            Previsão Anual
          </p>
          <h1 className="font-display" style={{ fontSize: 'clamp(1.75rem,5vw,2.5rem)', marginBottom: '0.5rem' }}>
            {sign.name} em 2027
          </h1>
          <p style={{ color: 'var(--text-secondary)', maxWidth: '480px', margin: '0 auto', lineHeight: 1.6 }}>
            {forecast.intro}
          </p>
        </div>

        <div style={{ maxWidth: '720px', margin: '0 auto', padding: '0 1.5rem 4rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '2rem', marginTop: '1.5rem' }}>
            {[
              { emoji: '💘', title: 'Amor e Relacionamentos', content: forecast.love },
              { emoji: '💼', title: 'Carreira e Finanças', content: forecast.career },
              { emoji: '🌿', title: 'Saúde e Bem-estar', content: forecast.health },
            ].map(({ emoji, title, content }) => (
              <div key={title} className="card">
                <h2 className="font-display" style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>{emoji} {title}</h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', lineHeight: 1.8 }}>{content}</p>
              </div>
            ))}
          </div>

          <div className="card" style={{ marginBottom: '1.5rem', border: '1px solid rgba(245,200,66,0.25)', background: 'rgba(245,200,66,0.04)' }}>
            <h2 className="font-display" style={{ fontSize: '1.125rem', marginBottom: '0.75rem', color: 'var(--petrol)' }}>
              ✦ O Melhor Período do Ano
            </h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>{forecast.highlight}</p>
          </div>

          <div className="card" style={{ marginBottom: '2rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.125rem', marginBottom: '0.75rem' }}>
              🌀 Principal Desafio
            </h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>{forecast.challenge}</p>
          </div>

          {/* CTA */}
          <div className="card" style={{ textAlign: 'center', border: '1px solid rgba(245,200,66,0.2)', background: 'linear-gradient(135deg, rgba(245,200,66,0.06) 0%, transparent 100%)' }}>
            <p className="font-display" style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>
              Receba previsões personalizadas com base no seu mapa natal →
            </p>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '1.25rem' }}>
              A previsão do seu signo é só o começo. Com o Professya, você recebe análises baseadas em todos os planetas do seu mapa, muito mais preciso e pessoal.
            </p>
            <Link to="/register" className="btn btn-primary">✦ Criar conta grátis</Link>
          </div>

          {/* Other signs */}
          <div style={{ marginTop: '2rem' }}>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', marginBottom: '0.75rem' }}>Previsão 2027 para outros signos:</p>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {SIGNS.filter(s => s.slug !== signo).map(s => (
                <Link key={s.slug} to={`/previsao-2027/${s.slug}`}
                  style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', backgroundColor: 'var(--bg-card)', padding: '0.25rem 0.625rem', borderRadius: 'var(--radius-full)', border: '1px solid var(--border-subtle)', textDecoration: 'none' }}>
                  {s.emoji} {s.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
